import React from 'react';
import styled from 'styled-components';
import ProjectTeeth from './ProjectTeeth';
import { useCreateProjectTeeth } from 'lib/hooks/project/useCreateProjectTeeth';
import CreateProjectIndicationContainer from 'containers/project/clinic/CreateProjectIndicationContainer';
import CreateProjectOptionContainer from 'containers/project/clinic/CreateProjectOptionContainer';
import CreateProjectPartner from './CreateProjectPartner';
import MuiButton from 'components/common/button/MuiButton';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import { IconButton } from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import ProjectTeethPhotogrammetry from './ProjectTeethPhotogrammetry';
import { useProjectDetailStore } from 'store/local/projectDetail';
import cx from 'classnames';
import CreateProjectOptionPim from './CreateProjectOptionPim';

interface CreateProjectContentProps {
  from: 'create' | 'detail';
}

const CreateProjectContent = ({ from }: CreateProjectContentProps) => {
  const {
    numbering,
    setNumbering,
    teeth,
    setTeeth,
    tooth,
    setTooth,
    bridge,
    setBridge,
    copyData,
    setCopyData,
    teethContextActions,
    toothSvgRef,
    isValidCreateProject,
    onToggleTooth,
    onChangeToothValue,
    onNumberingClick,
    onSaveIndication,
    onCancelIndication,
    onCopyTeeth,
    onToggleToothPhotogrammetry,
  } = useCreateProjectTeeth();
  const { isPhotogrammetry } = useProjectDetailStore();

  return (
    <StyledCreateProjectContent>
      {from === 'detail' && (
        <div className="createProject__title_box">
          <CustomText fontSize={26} fontWeight={600} fontColor="#222">
            <T>PROJECT_INDICATION_TITLE</T>
          </CustomText>
          <IconButton onClick={onCancelIndication}>
            <Clear />
          </IconButton>
        </div>
      )}
      <div className={`createProject__box teeth`}>
        <div className="teeth_box">
          {isPhotogrammetry ? (
            <ProjectTeethPhotogrammetry
              numbering={numbering}
              setNumbering={setNumbering}
              tooth={tooth}
              setTooth={setTooth}
              teeth={teeth}
              setTeeth={setTeeth}
              bridge={bridge}
              setBridge={setBridge}
              copyData={copyData}
              setCopyData={setCopyData}
              teethContextActions={teethContextActions}
              onToggleTooth={onToggleToothPhotogrammetry}
              onChangeToothValue={onChangeToothValue}
              onNumberingClick={onNumberingClick}
            />
          ) : (
            <ProjectTeeth
              numbering={numbering}
              setNumbering={setNumbering}
              tooth={tooth}
              setTooth={setTooth}
              teeth={teeth}
              setTeeth={setTeeth}
              bridge={bridge}
              setBridge={setBridge}
              copyData={copyData}
              setCopyData={setCopyData}
              teethContextActions={teethContextActions}
              onToggleTooth={onToggleTooth}
              onChangeToothValue={onChangeToothValue}
              onNumberingClick={onNumberingClick}
              onCopyTeeth={onCopyTeeth}
            />
          )}
        </div>
      </div>
      <div className={`createProject__box indication`}>
        <CreateProjectIndicationContainer />
      </div>
      <div className={cx(`createProject__box option ${from}`, { pim: isPhotogrammetry })}>
        <CreateProjectOptionContainer />
      </div>
      {isPhotogrammetry && (
        <div className={`createProject__box pim`}>
          <CreateProjectOptionPim />
        </div>
      )}
      {from === 'create' && (
        <div className={`createProject__box partner ${from}`}>
          <CreateProjectPartner />
        </div>
      )}
      {from === 'detail' && (
        <div className="createProject__button_box">
          <MuiButton
            config={{
              width: '190px',
            }}
            disableElevation
            variant="outlined"
            className="project__modify"
            onClick={() => onCancelIndication()}
          >
            <T>GLOBAL_CANCEL</T>
          </MuiButton>
          <MuiButton
            config={{
              width: '190px',
            }}
            disabled={!isValidCreateProject}
            disableElevation
            variant="contained"
            className="project__modify"
            onClick={() => onSaveIndication()}
          >
            <T>GLOBAL_SAVE</T>
          </MuiButton>
        </div>
      )}
    </StyledCreateProjectContent>
  );
};

const StyledCreateProjectContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr; /* 3개의 컬럼 */
  grid-template-rows: auto 1fr auto auto; /* 4개의 행 (첫 번째 행은 title_box용) */
  column-gap: 40px;
  row-gap: 20px;
  .createProject__title_box {
    grid-column: 1 / 4; /* 1번째부터 3번째 컬럼 전체 차지 */
    grid-row: 1 / 2; /* 첫 번째 행 전체 차지 */
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .createProject__box {
    width: 100%;
    border-radius: 10px;
    border: 1px solid #e1e1e7;
    background: #fff;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.16);
    &.teeth {
      grid-column: 1 / 2; /* 1번째 열에 위치 */
      grid-row: 2 / 4; /* 두 번째부터 세 번째 행까지 차지 */
      height: 824px;
      .teeth_box {
        height: 100%;
        padding: 30px;
      }
    }
    &.indication {
      overflow: hidden;
      grid-column: 2 / 3; /* 2번째 열에 위치 */
      grid-row: 2 / 4; /* 두 번째부터 세 번째 행까지 차지 */
      height: 824px;
    }
    &.option {
      &.detail {
        overflow: hidden;
        grid-column: 3 / 4; /* 3번째 열에 위치 */
        grid-row: 2 / 4; /* 두 번째부터 세 번째 행까지 차지 */
        height: 824px;
        &.pim {
          height: 642px;
        }
      }
      &.create {
        overflow: hidden;
        grid-column: 3 / 4; /* 3번째 열에 위치 */
        grid-row: 2 / 3; /* 두 번째 행만 차지 */
        height: 642px;
      }
    }
    &.pim {
      grid-column: 3 / 4; /* 3번째 열에 위치 */
      grid-row: 3 / 4; /* 세 번째 행만 차지 */
    }
    &.partner {
      grid-column: 3 / 4; /* 3번째 열에 위치 */
      grid-row: 3 / 4; /* 세 번째 행만 차지 */
    }
  }
  .createProject__button_box {
    grid-column: 1 / 4; /* 1번째부터 3번째 컬럼 전체 차지 */
    grid-row: 4 / 5; /* 네 번째 행 전체 차지 */
    height: 100%; /* 자동으로 행 높이에 맞게 조정 */
    display: flex;
    justify-content: center;
    column-gap: 16px;
  }
`;

export default CreateProjectContent;
